import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getCustomerNegotiationDetail,
  addCommentToCustomerNegotiation,
  approveCustomerNegotiation,
  CustomerVersioncomparesion,
  customerNegotiationGenerateSummary,
} from "../../services/CommonService";

import ReviewHeader from "./ReviewHeader";
import ReviewFooter from "./ReviewFooter";
import OTPVerification from "./OTPVerification";
import SummaryModal from "./SummaryModal";
import NegotiationContent from "./NegotiationContent";
import NegotiationInfo from "./NegotiationInfo";
import VersionComparisonModal from "./VersionComparisonModal";

import { FaPaperPlane, FaCheckCircle, FaFileAlt } from "react-icons/fa";
import ModalComponent from "../../components/ModalComponent";

import Utils from "../../utils";

import "./NegotiationReview.css";


/* ------------------------ CONFIRM MODAL ------------------------ */
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, loading }) => (
  <ModalComponent
    show={isOpen}
    handleClose={!loading ? onClose : undefined}
    title={title}
    size="sm"
    footerButtons={[
      {
        label: "Cancel",
        variant: "secondary",
        onClick: onClose,
        disabled: loading,
      },
      {
        label: loading ? "Processing..." : "Confirm",
        variant: "green",
        onClick: onConfirm,
        disabled: loading,
      },
    ]}
  >
    <div className="confirmation-modal-body">
      <p className="confirmation-message text-muted mb-0">{message}</p>
    </div>
  </ModalComponent>
);


/* =========================== MAIN REVIEW =========================== */
const NegotiationReview = () => {
  const { id } = useParams();
  const [negotiationData, setNegotiationData] = useState(null);
  const [negotiationId, setNegotiationId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [allComments, setAllComments] = useState([]);
  const [apiComments, setApiComments] = useState([]);

  const [otpVerified, setOtpVerified] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState("");

  const [isSendingComments, setIsSendingComments] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  const [hasUserTakenAction, setHasUserTakenAction] = useState(false);
  const [isReadOnlyMode, setIsReadOnlyMode] = useState(false);

  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [showContractInfo, setShowContractInfo] = useState(false);
  const [contractDetails, setContractDetails] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isNewCommentAdded, setIsNewCommentAdded] = useState(false);
  const [userSubmittedOnce, setUserSubmittedOnce] = useState(false);

  const clearDraftCommentsRef = useRef(null);


  /* -------- VERSION COMPARISON STATES -------- */
  const [comparisonData, setComparisonData] = useState(null);
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const [versionLoading, setVersionLoading] = useState(false);



  /* ---------------------- FETCH NEGOTIATION ---------------------- */
  const fetchNegotiationDetails = useCallback(async () => {
    try {
      const res = await getCustomerNegotiationDetail({ uuid: id });
      const data = res?.data?.data;

      setNegotiationData(data);
      setNegotiationId(data.id);

      setCurrentUserEmail(data.recipient_detail?.email);
      setContractDetails(data);

      const parsed =
        data.comments?.map((c) => ({
          id: `api-${c.id}`,
          comment_text: c.comment_text,
          position: c.position,
          selectedText: c.selected_text || "",
          sent: true,
          recipient_name: c.recipient_name,
          recipient_email: c.recipient_email,
          created_at: c.created_at,
        })) || [];

      setApiComments(parsed);

      const hasSentComments =
        data.comments?.some(
          (c) => c.recipient_email === data.recipient_detail?.email
        ) || false;

      if (hasSentComments) {
        setUserSubmittedOnce(true);
        setHasUserTakenAction(true);
      }
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);


  useEffect(() => {
    fetchNegotiationDetails();
  }, [fetchNegotiationDetails]);


  /* ---------------------- HANDLE COMMENTS ---------------------- */
  const handleCommentsUpdate = (updated) => {
    setAllComments(updated);
  };

  const clearLocalDraftComments = () => {
    clearDraftCommentsRef.current?.();
  };

  const unsentComments = allComments.filter((c) => !c.sent);


  /* ---------------------- SEND COMMENTS ---------------------- */
  const handleSendAllComments = async () => {
    if (unsentComments.length === 0) {
      return toast.error("Please add at least one comment before submitting");
    }

    try {
      setIsSendingComments(true);

      const payload = {
        uuid: id,
        version_id: negotiationData?.current_version,
        comments: unsentComments.map((c) => ({
          comment_text: c.comment_text,
          selected_text: c.selectedText,
          position: c.position,
        })),
      };

      const res = await addCommentToCustomerNegotiation(payload); // single API call

      toast.success(res?.data?.message);

      clearLocalDraftComments();

      setHasUserTakenAction(true);
      setUserSubmittedOnce(true);
      setIsNewCommentAdded(false);

      await fetchNegotiationDetails();
    } catch (error) {
      toast.error(Utils.getErrorMessage(error));
    } finally {
      setIsSendingComments(false);
    }
  };

  /* ---------------------- APPROVE NEGOTIATION ---------------------- */
  const handleConfirmApproval = async () => {
    try {
      setIsApproving(true);

      const res = await approveCustomerNegotiation({ uuid: id });

      if (res?.data?.success || res?.status === 200) {
        toast.success(res?.data?.message);

        setIsReadOnlyMode(true);
        setHasUserTakenAction(true);
        setUserSubmittedOnce(true);

        localStorage.setItem(`approved_${currentUserEmail}_${id}`, "true");

        await fetchNegotiationDetails();
        setShowConfirmModal(false);
      }
    } catch (error) {
      toast.error(Utils.getErrorMessage(error));
    } finally {
      setIsApproving(false);
    }
  };


  /* ---------------------- LOAD VERSION COMPARISON ---------------------- */
  const loadVersionComparison = async () => {
    setVersionLoading(true);

    try {
      const response = await CustomerVersioncomparesion({ negotiation_id: id });

      if (response?.data?.data) {
        setComparisonData(response.data.data);
      }
    } catch (error) {
      toast.error(Utils.getErrorMessage(error));
    } finally {
      setVersionLoading(false);
    }
  };


  /* ---------------------- FETCH SUMMARY ---------------------- */
  const fetchSummary = async () => {
    if (!negotiationData?.id) {
      toast.error("Negotiation data not available");
      return;
    }

    setIsSummaryLoading(true);

    try {
      const payload = {
       uuid: id
      };

      const response = await customerNegotiationGenerateSummary(payload);

      if (response?.data?.data) {
        // Extract the summary string from the response
        // The API might return an object with a 'summary' field or directly a string
        const summaryText = typeof response.data.data === 'string' 
          ? response.data.data 
          : response.data.data.summary || response.data.data.contract_summary || JSON.stringify(response.data.data, null, 2);
        
        setSummaryData(summaryText);
        setShowSummaryModal(true);
      } else {
        toast.error("Failed to generate summary");
      }
    } catch (error) {
      toast.error(Utils.getErrorMessage(error));
    } finally {
      setIsSummaryLoading(false);
    }
  };


  /* ---------------------- OTP CHECK ---------------------- */
  if (!otpVerified && negotiationData && negotiationId) {
    return (
      <OTPVerification
        negotiationId={negotiationId}
        negotiationData={negotiationData}
        recipientEmail={currentUserEmail}
        onVerifySuccess={() => setOtpVerified(true)}
      />
    );
  }


  /* ---------------------- LOADER ---------------------- */
  if (loading || !negotiationData) {
    return (
      <div className="page-loading">
        <img src="/images/loader.gif" alt="loader" />
      </div>
    );
  }


  /* ---------------------- MAIN UI ---------------------- */
  return (
    <>
      {(isSendingComments || isApproving) && (
        <div className="page-loading" style={{ position: "fixed", zIndex: 9999 }}>
          <img src="/images/loader.gif" alt="loader" />
        </div>
      )}

      <div className="review-page-wrapper">
        <ReviewHeader
          negotiationData={negotiationData}
          onContractInfoClick={() => setShowContractInfo(true)}
        />


        <div className="review-main-content">

          {/* ----------------- VERSION COMPARE BUTTON ----------------- */}
          <button
            className="version-compare-btn"
            onClick={() => {
              loadVersionComparison();
              setIsVersionModalOpen(true);
            }}
          >
            Version Comparison
          </button>


          <div className="review-content-scroll-area">
            <NegotiationContent
              htmlContent={negotiationData.html_content}
              onCommentsUpdate={handleCommentsUpdate}
              onNewCommentAdded={() => setIsNewCommentAdded(true)}
              currentUserEmail={currentUserEmail}
              apiComments={apiComments}
              clearDraftCommentsRef={clearDraftCommentsRef}
              isReadOnly={isReadOnlyMode}
              id={negotiationData.id}
            />
          </div>


          {/* ---------------- ACTION BUTTONS ---------------- */}
          <div className="review-actions-container">
            <button 
              onClick={fetchSummary} 
              disabled={isSummaryLoading}
              className="summary-btn"
            >
              <FaFileAlt /> {isSummaryLoading ? "Generating..." : "View Summary"}
            </button>

            {/* Approve Button */}
            <button
              onClick={() => setShowConfirmModal(true)}
              disabled={
                hasUserTakenAction ||
                isSendingComments ||
                isApproving ||
                unsentComments.length > 0 ||
                isNewCommentAdded ||
                userSubmittedOnce
              }
              className="review-approve-button"
            >
              <FaCheckCircle /> Approve as-is
            </button>

            {/* Submit Comments */}
            <button
              onClick={handleSendAllComments}
              // disabled={isSendingComments || isApproving || unsentComments.length === 0}
              disabled={isSendingComments || isApproving}
              className="review-submit-comments-button"
            >
              <FaPaperPlane /> Submit ({unsentComments.length})
            </button>
          </div>
        </div>


        {/* <ReviewFooter negotiationData={negotiationData} /> */}

        {/* Summary Modal */}
        <SummaryModal
          isOpen={showSummaryModal}
          onClose={() => {
            setShowSummaryModal(false);
            setSummaryData(null);
          }}
          summary={summaryData}
          isLoading={isSummaryLoading}
        />

        {/* Approval Confirm Modal */}
        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => !isApproving && setShowConfirmModal(false)}
          onConfirm={handleConfirmApproval}
          title="Approve Negotiation"
          message="After approving, you will not be able to add comments. Are you sure you want to approve this negotiation as-is?"
          loading={isApproving}
        />
      </div>


      {/* Contract Detail Modal */}
      <NegotiationInfo
        show={showContractInfo}
        handleClose={() => setShowContractInfo(false)}
        contract={contractDetails}
      />


      {/* VERSION COMPARISON MODAL */}
      <VersionComparisonModal
        isOpen={isVersionModalOpen}
        onClose={() => setIsVersionModalOpen(false)}
        comparisonData={comparisonData}
        loading={versionLoading}
      />
    </>
  );
};

export default NegotiationReview;
